import { helmAccess, type HelmAccess } from './roles'

/**
 * Helm's menu: what each login sees, and in which group. The shell draws it;
 * the guided tours point at its items by `tourName`.
 */

export type MenuGroupId = 'overview' | 'shipments' | 'people' | 'workspace'

export interface MenuItem {
  title: string
  url: string
  /** `section` is active on the page and the pages under it. */
  match: 'exact' | 'section'
  group: MenuGroupId
}

export interface MenuGroup {
  id: MenuGroupId
  label: string
  items: MenuItem[]
}

const GROUPS: ReadonlyArray<{ id: MenuGroupId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'shipments', label: 'Shipments' },
  { id: 'people', label: 'People' },
  { id: 'workspace', label: 'Workspace' },
]

export const MENU_ITEMS: readonly MenuItem[] = [
  { title: 'Dashboard', url: '/', match: 'exact', group: 'overview' },
  { title: 'Attention', url: '/attention', match: 'section', group: 'overview' },
  { title: 'Beacon', url: '/assistant', match: 'section', group: 'overview' },
  { title: 'Inquiries', url: '/inquiries', match: 'section', group: 'shipments' },
  { title: 'Consignments', url: '/consignments', match: 'section', group: 'shipments' },
  { title: 'Parcels', url: '/parcels', match: 'section', group: 'shipments' },
  { title: 'Warehouse', url: '/warehouse', match: 'section', group: 'shipments' },
  { title: 'Customers', url: '/customers', match: 'section', group: 'people' },
  { title: 'Users', url: '/settings/users', match: 'section', group: 'people' },
  { title: 'Settings', url: '/settings', match: 'exact', group: 'workspace' },
  { title: 'Documentation', url: '/docs', match: 'section', group: 'workspace' },
]

export interface CreateItem {
  title: string
  /** What the New menu calls it. */
  label: string
  url: string
}

export const CREATE_ITEMS: readonly CreateItem[] = [
  { title: 'New Inquiry', label: 'Inquiry', url: '/inquiries/new' },
  { title: 'New Consignment', label: 'Consignment', url: '/consignments/new' },
  { title: 'New Customer', label: 'Customer', url: '/customers/new' },
]

/** "New Inquiry" is `nav-new-inquiry`: what the guided tours point at. */
export const tourName = (title: string) => `nav-${title.toLowerCase().replaceAll(' ', '-')}`

export function isMenuItemActive(pathname: string, item: Pick<MenuItem, 'url' | 'match'>) {
  if (item.match === 'section') {
    if (pathname === `${item.url}/new`) return false
    return pathname === item.url || pathname.startsWith(`${item.url}/`)
  }
  return pathname === item.url
}

/**
 * The menu for a login. The warehouse team sees the warehouse and nothing
 * else, as a plain list. Everyone else sees the groups, without the
 * Warehouse unless they work there or are an admin, and with Parcels, a
 * customer's own page for sending to the warehouse, only for customers. A
 * group left with nothing in it is not shown. Until the profile is known,
 * the menu is what a login with no roles sees.
 */
export function navigationFor(access: HelmAccess | null): {
  /** No group headings: the warehouse team's short menu. */
  flat: boolean
  groups: MenuGroup[]
  create: readonly CreateItem[]
} {
  const { warehouseOnly, seesWarehouse, customer } = access ?? helmAccess(null)
  const visible = MENU_ITEMS.filter((item) =>
    warehouseOnly
      ? item.url === '/warehouse' || item.url === '/docs'
      : (item.url !== '/warehouse' || seesWarehouse) && (item.url !== '/parcels' || customer),
  )
  const groups = GROUPS.map((group) => ({
    ...group,
    items: visible.filter((item) => item.group === group.id),
  })).filter((group) => group.items.length > 0)
  return { flat: warehouseOnly, groups, create: warehouseOnly ? [] : CREATE_ITEMS }
}

/** The group holding the page someone is on: it is always open. */
export function groupHolding(pathname: string, groups: readonly MenuGroup[]) {
  return groups.find((group) => group.items.some((item) => isMenuItemActive(pathname, item)))?.id
}

/** Collapsed groups, as kept in the browser; anything unexpected is ignored. */
export function parseCollapsed(raw: string | null): MenuGroupId[] {
  if (!raw) return []
  try {
    const value = JSON.parse(raw) as unknown
    if (!Array.isArray(value)) return []
    const known = new Set<string>(GROUPS.map((group) => group.id))
    return value.filter((id): id is MenuGroupId => typeof id === 'string' && known.has(id))
  } catch {
    return []
  }
}
